import { useState } from 'react';
import { AppFormDialog, AppMenuSheet, AppPickerRow, AppTextField } from '@core/designsystem';
import type { CaseType } from '@core/domain/model';
import type { ClientDraft } from '@core/ui/NewClientDialog';
import { ClientPicker } from './ClientPicker';
import { typeLabel } from './formatter';
import type { ClientOption } from './types';

const TYPES: CaseType[] = ['CourtCase', 'ClaimWork', 'Consultation', 'Enforcement', 'Other'];

/**
 * Создание дела.
 *
 * Обязательное поле одно — название («Структура» §12): дело заводят на ходу, между
 * заседаниями, и анкета из шести полей здесь означала бы, что дело не заведут вовсе.
 * Тип и клиент — по желанию, остальное правится в карточке.
 *
 * Трёхшагового мастера из исходников нет намеренно: решение владельца 30.08.2026.
 */
export function NewCaseDialog({
  clients,
  onDismiss,
  onCreate,
  onCreateClient,
}: {
  clients: ClientOption[];
  onDismiss: () => void;
  onCreate: (draft: {
    title: string;
    type: CaseType;
    typeOther: string | null;
    clientId: string | null;
  }) => void;
  /** Заводит клиента и возвращает его идентификатор — он сразу встаёт в это дело. */
  onCreateClient: (draft: ClientDraft) => Promise<string | null>;
}) {
  const [title, setTitle] = useState('');
  const [type, setType] = useState<CaseType>('CourtCase');
  const [typeOther, setTypeOther] = useState('');
  const [clientId, setClientId] = useState<string | null>(null);
  const [typeOpen, setTypeOpen] = useState(false);
  const [clientOpen, setClientOpen] = useState(false);

  const [addedClient, setAddedClient] = useState<ClientOption | null>(null);

  /*
   * Имя нового клиента берётся из того, что мы только что завели.
   *
   * Список `clients` приходит сверху и перечитывается асинхронно: пока он не доехал,
   * строка показывала бы «Не выбран» у дела, клиент которого уже создан.
   */
  const known = addedClient ? [addedClient, ...clients] : clients;
  const clientName = known.find((client) => client.id === clientId)?.name ?? 'Не выбран';

  const create = () => {
    const trimmed = title.trim();
    if (!trimmed) return;

    onCreate({
      title: trimmed,
      type,
      typeOther: type === 'Other' ? (typeOther.trim() || null) : null,
      clientId,
    });
  };

  return (
    <>
      <AppFormDialog
        title="Новое дело"
        onDismiss={onDismiss}
        confirm={{ label: 'Создать', onClick: create }}
      >
        <AppTextField value={title} onChange={setTitle} label="Название" autoFocus />

        <AppPickerRow
          label="Тип"
          value={typeLabel({ type, typeOther: typeOther || null })}
          onClick={() => setTypeOpen(true)}
        />

        {/* Своё название типа спрашиваем только у «Другого»: «Другое» в списке
            из десяти дел юристу ничего не говорит. */}
        {type === 'Other' ? (
          <AppTextField value={typeOther} onChange={setTypeOther} label="Название типа" />
        ) : null}

        <AppPickerRow label="Клиент" value={clientName} onClick={() => setClientOpen(true)} />
      </AppFormDialog>

      {typeOpen ? (
        <AppMenuSheet
          title="Тип дела"
          onDismiss={() => setTypeOpen(false)}
          items={TYPES.map((value) => ({
            icon: value === type ? ('check' as const) : ('folder' as const),
            label: typeLabel({ type: value, typeOther: null }),
            onSelect: () => setType(value),
          }))}
        />
      ) : null}

      {clientOpen ? (
        <ClientPicker
          clients={known}
          onDismiss={() => setClientOpen(false)}
          onSelect={(id) => {
            setClientId(id);
            setClientOpen(false);
          }}
          onCreate={async (draft) => {
            const id = await onCreateClient(draft);
            if (!id) return;

            setAddedClient({ id, name: draft.fullName.trim() });
            setClientId(id);
            setClientOpen(false);
          }}
        />
      ) : null}
    </>
  );
}
